import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'social.rightSidebar.widgets';

const readStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const writeStorage = (value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    // Persistence is best-effort; ignore quota/serialization errors.
  }
};

const mergeWithDefaults = (stored, defaults) => {
  const defaultIds = new Set(defaults.map((item) => item.id));
  const pinnedIds = new Set(
    defaults.filter((item) => item.pinToTop).map((item) => item.id)
  );
  const seen = new Set();
  const pinned = [];
  const rest = [];

  // Pinned items always go first in their default order, ignoring stored order.
  defaults.forEach((item) => {
    if (!pinnedIds.has(item.id)) return;
    seen.add(item.id);
    const storedPref = Array.isArray(stored)
      ? stored.find((s) => s && s.id === item.id)
      : null;
    pinned.push({ id: item.id, enabled: storedPref?.enabled !== false });
  });

  if (Array.isArray(stored)) {
    stored.forEach((item) => {
      if (!item || typeof item.id !== 'string') return;
      if (!defaultIds.has(item.id)) return;
      if (seen.has(item.id)) return;
      seen.add(item.id);
      rest.push({ id: item.id, enabled: item.enabled !== false });
    });
  }

  defaults.forEach((item) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    rest.push({ id: item.id, enabled: item.enabled !== false });
  });

  return [...pinned, ...rest];
};

const useSidebarPreferences = (defaults) => {
  const [preferences, setPreferences] = useState(() =>
    mergeWithDefaults(readStorage(), defaults)
  );

  useEffect(() => {
    setPreferences((current) => {
      const reconciled = mergeWithDefaults(current, defaults);
      const sameLength = current.length === reconciled.length;
      const sameOrder =
        sameLength &&
        current.every(
          (item, index) =>
            reconciled[index] &&
            reconciled[index].id === item.id &&
            reconciled[index].enabled === item.enabled
        );
      return sameOrder ? current : reconciled;
    });
  }, [defaults]);

  useEffect(() => {
    writeStorage(preferences);
  }, [preferences]);

  const toggleWidget = useCallback((id) => {
    setPreferences((current) =>
      current.map((item) =>
        item.id === id ? { ...item, enabled: !item.enabled } : item
      )
    );
  }, []);

  const moveWidget = useCallback((id, direction) => {
    setPreferences((current) => {
      const index = current.findIndex((item) => item.id === id);
      if (index === -1) return current;
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= current.length) return current;
      const next = current.slice();
      const [item] = next.splice(index, 1);
      next.splice(newIndex, 0, item);
      return next;
    });
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferences(
      defaults.map((item) => ({ id: item.id, enabled: item.enabled !== false }))
    );
  }, [defaults]);

  return {
    preferences,
    toggleWidget,
    moveWidget,
    resetPreferences,
  };
};

export default useSidebarPreferences;
